import {MahkemeBirimleriRow} from "@/plugins/uyap-plugin/uyap/CommonInterfaces";
import {metinTemizle} from "@/utils/metinNormalize";

/**
 * Excel'den gelen dosya listesinin çözümlenmesi (Faz 3, DEV-2966 manuel test
 * düzeltmesi).
 *
 * NEDEN EXCEL: plan ekranında önceden "elle birim id + esas no" girişi vardı;
 * manuel testte kullanıcıların birim kimliğini BİLMEDİĞİ görüldü. Repodaki
 * yerleşik çözüm Excel yüklemedir (toplu sorgu / Genel İşlem sihirbazı aynısını
 * kullanıyor): kullanıcı yalnız `birimAdi` ve `dosyaNo` yazar, `birim_id`
 * UYAP'ın daire listesinden ÇÖZÜLÜR.
 *
 * BU MODÜL SAF: XLSX okuma, UYAP çağrısı ve DOM burada yok — hepsi çağıranda.
 * Böylece eşleştirme kuralları (yanlış dosyada işlem yapma riskinin kaynağı)
 * bileşen mount etmeden test edilebiliyor.
 *
 * Kurallar `GenelIslemFormWizard`in Excel yolundan alındı ve BİLEREK birebir
 * tutuldu: iki ekran aynı Excel dosyasını kabul etmeli, biri kabul edip diğeri
 * reddederse kullanıcı hangi ekranda ne olduğunu anlayamaz.
 */

/** UYAP esas no; talimat biçimi de kabul edilir ("2023/41114 - (2023/999)"). */
export const EXCEL_ESAS_NO_REGEX = /^\d{4}\/\d+(?:\s*-\s*\(\s*\d{4}\/\d+\s*\))?$/;

/**
 * Ad normalizasyonu TEK KAYNAKTAN gelir (`$uyapHelper`). Bu modül saf kalsın diye
 * fonksiyonlar dışarıdan enjekte edilir; kopyalanmazlar, çünkü görev koşarken
 * `DosyaBul` aynı kuralları uyguluyor ve kurallar ayrışırsa burada kabul edilen
 * satır gece koşumunda eşleşmez.
 */
export interface BirimEslestirici {
    normalize(birimAdi: any): string;
    kanonik(birimAdi: any): string;
}

/** Çözümlenmiş Excel satırı — `IAvukatDosya` ile alan-uyumlu. */
export interface ExcelDosyaSatiri {
    birimAdi: string;
    dosyaNo: string;
    birimId: string;
    /** UYAP'taki gerçek daire adı; kullanıcıya farkı göstermek için. */
    eslesenBirimAdi: string;
    index: number;
    rowOrder: number;
}

// kod inceleme: gövde genelIslemTakipFiltre.ts'teki (özel) metin() ile birebir
// aynıydı (iki bağımsız kopya); paylaşılan @/utils/metinNormalize'a taşındı.
// Burada yeniden dışa aktarılıyor ki mevcut çağıranlar/testler `metinTemizle`
// adını (ve BU MODÜLÜN SAFLIĞINI — @/utils/metinNormalize'da başka bağımlılık
// yok) korusun.
export {metinTemizle};

/** `birimBul` için önceden kurulmuş arama indeksi (bkz. `birimIndeksiGetir`). */
interface BirimIndeksi {
    /** normalize-anahtar → daire (ilk eşleşen; `Array.find` sırasıyla birebir). */
    normalizeMap: Map<string, MahkemeBirimleriRow>;
    /** kanonik-anahtar → o anahtara sahip TÜM daireler (`Array.filter` ile birebir). */
    kanonikMap: Map<string, Array<MahkemeBirimleriRow>>;
}

/**
 * `daireler` dizisinin referansına göre kurulmuş indeksi önbelleğe alır.
 *
 * NEDEN WeakMap+REFERANS: liste satırlar arasında değişmiyor (aynı Excel/UYAP
 * sorgusu içinde hep aynı dizi nesnesi geçiyor); referans değişmediği sürece
 * aynı indeks yeniden kullanılır. Liste yeniden çekilirse (`this.icraDaireleri =
 * response.items` gibi) yeni bir dizi nesnesi gelir ve indeks kendiliğinden
 * yeniden kurulur — WeakMap sayesinde eski dizi GC'den de düşer.
 */
const birimIndeksOnbellek = new WeakMap<Array<MahkemeBirimleriRow>, BirimIndeksi>();

function birimIndeksiOlustur(
    liste: Array<MahkemeBirimleriRow>,
    eslestirici: BirimEslestirici
): BirimIndeksi {
    const normalizeMap = new Map<string, MahkemeBirimleriRow>();
    const kanonikMap = new Map<string, Array<MahkemeBirimleriRow>>();

    for (const daire of liste) {
        const normAnahtar = eslestirici.normalize(daire.birimAdi);
        // `Array.find` ile birebir: anahtar tekrar ederse İLKİ kazanır.
        if (normAnahtar && !normalizeMap.has(normAnahtar)) {
            normalizeMap.set(normAnahtar, daire);
        }

        const kanonikAnahtar = eslestirici.kanonik(daire.birimAdi);
        if (kanonikAnahtar) {
            const adaylar = kanonikMap.get(kanonikAnahtar);
            if (adaylar) adaylar.push(daire);
            else kanonikMap.set(kanonikAnahtar, [daire]);
        }
    }

    return {normalizeMap, kanonikMap};
}

function birimIndeksiGetir(
    liste: Array<MahkemeBirimleriRow>,
    eslestirici: BirimEslestirici
): BirimIndeksi {
    if (liste.length === 0) return {normalizeMap: new Map(), kanonikMap: new Map()};

    const onbellekteki = birimIndeksOnbellek.get(liste);
    if (onbellekteki) return onbellekteki;

    const indeks = birimIndeksiOlustur(liste, eslestirici);
    birimIndeksOnbellek.set(liste, indeks);
    return indeks;
}

/**
 * Daire adından UYAP kaydını bulur.
 *
 * Önce normalize tam eşleşme; tutmazsa kanonik form denenir. KANONİK FORMDA
 * BİRDEN ÇOK ADAY VARSA `null` döner — belirsizliği "ilkini seç" diye çözmek,
 * gece koşumunda YANLIŞ DOSYADA işlem yapmak demektir.
 *
 * PERFORMANS: `daireler` her çağrıda baştan taranmaz — dizi REFERANSINA göre
 * normalize/kanonik Map'lerine önbelleklenir (bkz. `birimIndeksiGetir`), böylece
 * bir Excel/UYAP listesindeki N satır için indeks yalnız BİR kez kurulur ve her
 * satır O(1) sürer (DEV-2603 B05: önceden her satır 355 daireyi baştan tarıyordu).
 */
export function birimBul(
    daireler: Array<MahkemeBirimleriRow>,
    birimAdi: any,
    eslestirici: BirimEslestirici
): MahkemeBirimleriRow | null {
    const liste = daireler ?? [];
    const aranan = eslestirici.normalize(birimAdi);
    if (!aranan) return null;

    const indeks = birimIndeksiGetir(liste, eslestirici);

    const tam = indeks.normalizeMap.get(aranan);
    if (tam) return tam;

    const kanonik = eslestirici.kanonik(birimAdi);
    if (!kanonik) return null;

    const adaylar = indeks.kanonikMap.get(kanonik);
    return adaylar && adaylar.length === 1 ? adaylar[0] : null;
}

/**
 * Ham `sheet_to_json` çıktısını çözümlenmiş satırlara çevirir.
 *
 * Temizlik birim çözümünden ÖNCE yapılır: baştaki/sondaki tek bir boşluk yüzünden
 * `birimId` sessizce boş kalıyor ama ekranda ad temiz görünüyordu.
 */
export function excelSatirlariniCozumle(
    hamSatirlar: Array<any>,
    daireler: Array<MahkemeBirimleriRow>,
    eslestirici: BirimEslestirici
): Array<ExcelDosyaSatiri> {
    return (hamSatirlar ?? []).map((ham: any, sira: number) => {
        const birimAdi = metinTemizle(ham?.birimAdi);
        const dosyaNo = metinTemizle(ham?.dosyaNo);
        const daire = birimBul(daireler, birimAdi, eslestirici);

        return {
            birimAdi,
            dosyaNo,
            birimId: daire ? String(daire.birimId) : "",
            eslesenBirimAdi: daire ? String(daire.birimAdi) : "",
            index: sira,
            rowOrder: sira
        };
    });
}

/**
 * Çözümlenmiş satırları doğrular; kullanıcıya gösterilecek hata metinlerini döner
 * (boş dizi = geçerli).
 *
 * KISMİ KABUL YOK: tek bir satır bile çözülemiyorsa liste hiç yüklenmez. Gerekçe
 * `GenelIslemFormWizard` ile aynı — `birimId`si çözülemeyen satır gece koşumunda
 * dosyanın adından tahmin edilmesine ve YANLIŞ DOSYADA işlem yapılmasına yol açar.
 */
export function excelDogrula(
    satirlar: Array<ExcelDosyaSatiri>,
    daireSayisi: number
): string[] {
    if (!satirlar || satirlar.length === 0) {
        return ["Excel dosyasında listelenecek satır bulunamadı."];
    }

    // kod inceleme: yalnız BİRİ eksik olsa bile başlık hatası — aksi halde tek sütun
    // adı yanlış yazıldığında akış per-satır kontrolüne düşüp N satırı tek tek
    // "tanınmayan daire" diye listeleyen okunaksız bir hataya dönüşüyor.
    const ilk = satirlar[0];
    if (!ilk.birimAdi || !ilk.dosyaNo) {
        return ["Excel başlıkları birimAdi ve dosyaNo olmalıdır."];
    }

    if (daireSayisi === 0) {
        return [
            "İcra daireleri listesi alınamadığı için Excel'deki daire adları doğrulanamadı. " +
            "UYAP bağlantınızı kontrol edip tekrar deneyin."
        ];
    }

    const hatalar: string[] = [];
    const bozukEsasNo: string[] = [];
    const taninmayanDaire: string[] = [];

    satirlar.forEach((satir) => {
        // +2: Excel'de 1. satır başlık, kullanıcı 1'den saymaya başlıyor.
        const satirNo = satir.rowOrder + 2;
        if (!EXCEL_ESAS_NO_REGEX.test(satir.dosyaNo)) {
            bozukEsasNo.push(String(satirNo));
        }
        if (!satir.birimId) {
            taninmayanDaire.push(satirNo + ". satır: " + (satir.birimAdi || "(boş)"));
        }
    });

    if (bozukEsasNo.length > 0) {
        let mesaj =
            "Bir veya birden fazla dosya numarası hatalı görünüyor. " +
            "Tüm dosya numaralarının dosyaNo sütununda olduğunu kontrol edin.";
        if (bozukEsasNo.length !== satirlar.length) {
            mesaj += " Hatalı satırlar: " + bozukEsasNo.join(", ");
        }
        hatalar.push(mesaj);
    }

    if (taninmayanDaire.length > 0) {
        hatalar.push(
            "Aşağıdaki satırlardaki icra dairesi adı UYAP'taki hiçbir daireyle eşleşmedi. " +
            "Yanlış dosyada işlem yapılmaması için liste yüklenmedi. Daire adlarını UYAP'taki " +
            "yazımıyla birebir girin. → " + taninmayanDaire.join(" | ")
        );
    }

    return hatalar;
}
